import type { ReactNode } from "react";

import type { DenialPrevention } from "../types/DenialPrevention";

import type { ArasScoreRowId } from "./aras-score-legend-lines";

export type ArasScoreBand = "red" | "orange" | "green";

export const MEDICAL_COMPLEXITY_EXPANDED_TEXT =
  "The patient does not demonstrate sufficient medical complexity requiring daily MD management. Document multiple severe active conditions and ongoing complex treatments necessitating daily physician oversight.";

export interface ArasScoreCategoryRow {
  /**
   * Stable React key and accordion identity. For API-driven rows this is usually the criterion `id` as a string.
   * When `scoreTooltip` is omitted and `id` is a known {@link ArasScoreRowId}, the matching legend is used.
   */
  id: string;
  /**
   * When set (e.g. from `denial_prevention` mapping), score tooltips use this catalog row’s legend
   * even if `id` is a numeric string from the API.
   */
  legendRowId?: ArasScoreRowId;
  label: string;
  points: number;
  band: ArasScoreBand;
  /** Shown in the expandable panel when this row is open. */
  expandedDetail?: string;
  /**
   * Tooltip body for the score on the right; each row can differ.
   * When omitted, the default score tooltip uses this row’s `points` (1–3).
   */
  scoreTooltip?: ReactNode;
  /**
   * When `false` (e.g. API criterion title did not match a catalog legend), the score is plain text with no tooltip.
   * Omitted or `true`: show score tooltip when a legend or default copy applies.
   */
  scoreLegendTooltip?: boolean;
}

export interface ArasScorePopoverContentProps {
  onClose: () => void;
  /**
   * When present with a non-empty `criteria` array, rows and total score come from the API payload
   * (each criterion is one accordion: title = `criteria`, score, gray panel = `reasoning`).
   * Otherwise `categories` (or built-in mock data) is used.
   */
  denialPrevention?: DenialPrevention | null;
  /** Overrides sum of category points when set (including when using `denialPrevention`). */
  totalScore?: number;
  /** When omitted and `denialPrevention` is not used, built-in mock categories are used for UI review. */
  categories?: ArasScoreCategoryRow[];
  /**
   * Row `id` to include in the initially expanded set (others start collapsed; users may open multiple rows).
   * When omitted, defaults to `medical-complexity` if present, otherwise the first category row.
   */
  defaultExpandedId?: string | null;
}
